// Document scanner — extracts content elements with content-hash IDs.
// Stores a node reference map for incremental diffing.

import {
  activeNodeMap,
  setOriginalNodeMap,
  setActiveNodeMap,
  setLastSyncedNodeMap,
} from "../modules/state";

let _lastNodeMap = {}; // nodeId → { id, text, style, type } (legacy, kept for buildDelta)

export const diffNodes = (currentNodes) => {
  const currentMap = {};
  for (const n of currentNodes) currentMap[n.id] = n;

  const added = [];
  const removed = [];
  const changed = [];
  const unchanged = [];

  for (const id of Object.keys(currentMap)) {
    if (!_lastNodeMap[id]) added.push(id);
    else unchanged.push(id);
  }

  for (const id of Object.keys(_lastNodeMap)) {
    if (!currentMap[id]) removed.push(id);
  }

  return { added, removed, changed, unchanged };
};

// Update stored reference with current scan nodes
export const storeNodeReference = (nodes) => {
  _lastNodeMap = {};
  for (const n of nodes) {
    _lastNodeMap[n.id] = { id: n.id, text: n.text, style: n.style, type: n.type };
  }
};

// Called once at init — sets all three maps from the initial scan
export const initializeNodeMaps = (nodes) => {
  const map = {};
  for (const n of nodes) map[n.id] = { id: n.id, text: n.text, style: n.style, type: n.type, position: n.position };
  setOriginalNodeMap({ ...map });
  setActiveNodeMap({ ...map });
  setLastSyncedNodeMap({ ...map });
};

// Apply a scan result to activeNodeMap and return the diff vs previous active state
export const applyAndDiffToActive = (scanNodes) => {
  const currentMap = {};
  for (const n of scanNodes) currentMap[n.id] = { id: n.id, text: n.text, style: n.style, type: n.type, position: n.position };

  const added = [], removed = [];
  for (const id of Object.keys(currentMap)) {
    if (!activeNodeMap[id]) added.push(currentMap[id]);
  }
  for (const id of Object.keys(activeNodeMap)) {
    if (!currentMap[id]) removed.push(id);
  }
  setActiveNodeMap(currentMap);
  return { added, removed };
};

// Build delta payload from scan (null on first scan)
export const buildDelta = (scan) => {
  const diff = diffNodes(scan.nodes);

  if (diff.removed.length === 0 && diff.unchanged.length === 0) return null;
  if (diff.added.length === 0 && diff.removed.length === 0) return null;

  const nodeMap = {};
  for (const n of scan.nodes) nodeMap[n.id] = n;

  return {
    type: "delta",
    added: diff.added.map((id) => nodeMap[id]),
    removed: diff.removed,
    order: scan.nodes.map((n) => n.id),
    styles: scan.styles,
    toc: scan.toc,
    meta: scan.meta,
  };
};

// Resolve current Word selection to matching node IDs from last scan
export const resolveSelectionToNodes = async () => {
  if (Object.keys(_lastNodeMap).length === 0) return null;

  // Reverse lookup: "trimmedText|style" → node[]
  const textStyleIndex = {};
  for (const node of Object.values(_lastNodeMap)) {
    const key = `${(node.text || "").trim()}|${node.style}`;
    if (!textStyleIndex[key]) textStyleIndex[key] = [];
    textStyleIndex[key].push(node);
  }

  let result = null;

  await Word.run(async (context) => {
    const sel = context.document.getSelection();
    sel.load("text");

    const paras = sel.paragraphs;
    paras.load("items/text,items/style");

    await context.sync();

    const selText = (sel.text || "").trim();
    if (!selText) return;

    const paraItems = paras.items;
    if (paraItems.length === 0) return;

    const matched = [];
    const selLines = selText
      .split(/[\r\n]+/)
      .map((l) => l.trim())
      .filter(Boolean);

    for (let i = 0; i < paraItems.length; i++) {
      const p = paraItems[i];
      const fullText = (p.text || "").trim();
      const style = p.style || "Normal";

      if (!fullText) continue;

      const key = `${fullText}|${style}`;
      const candidates = textStyleIndex[key];
      if (!candidates || candidates.length === 0) continue;

      const usedIds = new Set(matched.map((m) => m.id));
      const node = candidates.find((c) => !usedIds.has(c.id)) || candidates[0];

      const isFirst = i === 0;
      const isLast = i === paraItems.length - 1;
      const isSinglePara = paraItems.length === 1;

      let partial = false;
      let selectedText = fullText;

      if (isSinglePara && selText.length < fullText.length) {
        partial = true;
        selectedText = selText;
      } else if (isFirst && !isSinglePara) {
        const firstSelLine = selLines[0] || "";
        if (firstSelLine.length < fullText.length && fullText.endsWith(firstSelLine)) {
          partial = true;
          selectedText = firstSelLine;
        }
      } else if (isLast && !isSinglePara) {
        const lastSelLine = selLines[selLines.length - 1] || "";
        if (lastSelLine.length < fullText.length && fullText.startsWith(lastSelLine)) {
          partial = true;
          selectedText = lastSelLine;
        }
      }

      const entry = { id: node.id, type: node.type, style: node.style, text: fullText, partial };
      if (partial) entry.selectedText = selectedText;

      matched.push(entry);
    }

    if (matched.length === 0) return;

    const wordCount = selText.split(/\s+/).filter(Boolean).length;

    result = {
      nodes: matched,
      nodeIds: matched.map((m) => m.id),
      fullNodeIds: matched.filter((m) => !m.partial).map((m) => m.id),
      partialNodeIds: matched.filter((m) => m.partial).map((m) => m.id),
      selectionText: selText,
      wordCount,
      charCount: selText.length,
      paraCount: matched.length,
    };
  });

  return result;
};

// Fast 53-bit string hash (cyrb53) → 16-char hex
const hashString = (str) => {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const n = 4294967296 * (2097151 & h2) + (h1 >>> 0);
  return n.toString(16).padStart(16, "0");
};

// Stable node ID from content + style (collision suffix for duplicates)
const makeNodeId = (text, style, occurrenceIndex = 0) => {
  const raw = `${style}|${text}`;
  const hash = hashString(raw);
  return occurrenceIndex > 0 ? `${hash}_${occurrenceIndex}` : hash;
};

// Stable table ID from cell values + style
const makeTableId = (values, style, occurrenceIndex = 0) => {
  const flat = (values || []).map((row) => row.join("\t")).join("\n");
  const raw = `table|${style}|${flat}`;
  const hash = hashString(raw);
  return occurrenceIndex > 0 ? `${hash}_${occurrenceIndex}` : hash;
};

const TOC_RE = /^toc\s*(\d+)$/i;
const isTocStyle = (style) => TOC_RE.test(style);
const tocLevel = (style) => {
  const m = style.match(TOC_RE);
  return m ? parseInt(m[1], 10) : null;
};

// Scan the active Word document → flat ordered list of content elements
export const scanDocument = async ({ skipPageNumbers = false } = {}) => {
  const nodes = [];
  const toc = [];
  const tableNodes = [];
  const styleCounts = {};

  await Word.run(async (context) => {
    const body = context.document.body;

    const paras = body.paragraphs;
    paras.load(
      "items/text,items/style,items/styleBuiltIn," +
        "items/isListItem,items/tableNestingLevel," +
        "items/lineSpacing,items/spaceAfter,items/spaceBefore"
    );

    const tables = body.tables;
    tables.load(
      "items/values,items/rowCount,items/style,items/styleBuiltIn," +
        "items/headerRowCount,items/nestingLevel,items/isUniform," +
        "items/alignment,items/width"
    );

    await context.sync();

    // Process paragraphs
    const idCounts = {};
    let runningLineCount = 0;
    let docNodeIndex = 0;

    for (let i = 0; i < paras.items.length; i++) {
      const p = paras.items[i];
      const text = (p.text || "").trim();
      const style = p.style || "Normal";
      const styleBuiltIn = p.styleBuiltIn || "";
      const isListItem = p.isListItem || false;
      const tableNesting = p.tableNestingLevel || 0;

      if (!text) continue;
      if (tableNesting > 0) continue;

      styleCounts[style] = (styleCounts[style] || 0) + 1;

      const isToc = isTocStyle(style);

      const baseId = makeNodeId(text, style);
      idCounts[baseId] = idCounts[baseId] || 0;
      const nodeId = makeNodeId(text, style, idCounts[baseId]);
      idCounts[baseId]++;

      const wc = text.split(/\s+/).filter(Boolean).length;
      const cc = text.length;

      const ls = p.lineSpacing || 12;
      const charsPerLine = Math.max(40, Math.round(80 * (12 / ls)));
      const estimatedLines = Math.max(1, Math.ceil(cc / charsPerLine));
      const lineStart = runningLineCount + 1;
      runningLineCount += estimatedLines;
      const lineEnd = runningLineCount;

      const node = {
        id: nodeId,
        type: isToc ? "toc" : isListItem ? "list_item" : "paragraph",
        text,
        style,
        styleBuiltIn,
        wordCount: wc,
        charCount: cc,
        position: {
          paragraphIndex: i,
          nodeIndex: docNodeIndex,
          lineRange: [lineStart, lineEnd],
          pageRange: [1, 1],
        },
        index: i,
      };
      docNodeIndex++;

      if (isToc) {
        node.tocLevel = tocLevel(style);
        toc.push({ level: node.tocLevel, title: text, nodeRef: nodeId });
      }

      if (isListItem && !isToc) {
        node.listLevel = null;
        node.listString = null;
      }

      nodes.push(node);
    }

    // Load list item details (level, listString)
    const listItemNodes = nodes.filter((n) => n.type === "list_item");
    if (listItemNodes.length > 0) {
      const listItemProxies = [];
      for (const n of listItemNodes) {
        const p = paras.items[n.index];
        const li = p.listItemOrNullObject;
        li.load("level,listString,siblingIndex");
        listItemProxies.push({ node: n, proxy: li });
      }

      await context.sync();

      for (const { node, proxy } of listItemProxies) {
        if (!proxy.isNullObject) {
          node.listLevel = proxy.level;
          node.listString = proxy.listString || "";
        }
      }
    }

    // Page numbers via range.pages (Desktop only)
    let hasPageApi = false;
    try {
      hasPageApi = Office.context.requirements.isSetSupported("WordApiDesktop", "1.2");
    } catch (_) {
      hasPageApi = false;
    }

    if (hasPageApi && nodes.length > 0 && !skipPageNumbers) {
      const BATCH_SIZE = 50;
      for (let batchStart = 0; batchStart < nodes.length; batchStart += BATCH_SIZE) {
        const batchEnd = Math.min(batchStart + BATCH_SIZE, nodes.length);
        const batchEntries = [];

        for (let b = batchStart; b < batchEnd; b++) {
          const n = nodes[b];
          const p = paras.items[n.index];
          const range = p.getRange();
          const pages = range.pages;
          pages.load("items");
          batchEntries.push({ node: n, pages });
        }

        await context.sync();

        for (const entry of batchEntries) {
          for (const page of entry.pages.items) {
            page.load("index");
          }
        }

        await context.sync();

        for (const { node, pages } of batchEntries) {
          if (pages.items.length > 0) {
            const indices = pages.items.map((pg) => pg.index);
            node.position.pageRange = [Math.min(...indices), Math.max(...indices)];
          }
        }
      }
    }

    // Process tables
    const tableIdCounts = {};

    for (let t = 0; t < tables.items.length; t++) {
      const table = tables.items[t];
      const values = table.values || [];
      const style = table.style || "";
      const styleBuiltIn = table.styleBuiltIn || "";
      const rowCount = table.rowCount || 0;
      const columnCount = values.length > 0 ? values[0].length : 0;
      const headerRowCount = table.headerRowCount || 0;
      const nestingLevel = table.nestingLevel || 1;
      const isUniform = table.isUniform;
      const alignment = table.alignment || "";
      const width = table.width || 0;

      if (nestingLevel > 1) continue;

      styleCounts[style] = (styleCounts[style] || 0) + 1;

      const baseId = makeTableId(values, style);
      tableIdCounts[baseId] = tableIdCounts[baseId] || 0;
      const nodeId = makeTableId(values, style, tableIdCounts[baseId]);
      tableIdCounts[baseId]++;

      tableNodes.push({
        id: nodeId,
        type: "table",
        style,
        styleBuiltIn,
        rowCount,
        columnCount,
        headerRowCount,
        nestingLevel,
        isUniform,
        alignment,
        width,
        values,
        position: { tableIndex: t },
      });
    }

    // Determine table positions among paragraphs
    if (tableNodes.length > 0) {
      const tableAnchors = [];
      for (let t = 0; t < tables.items.length; t++) {
        if ((tables.items[t].nestingLevel || 1) > 1) continue;
        const before = tables.items[t].getParagraphBeforeOrNullObject();
        before.load("text,style");
        tableAnchors.push(before);
      }

      await context.sync();

      for (let t = 0; t < tableNodes.length; t++) {
        const anchor = tableAnchors[t];
        if (!anchor.isNullObject) {
          const anchorText = (anchor.text || "").trim();
          const anchorStyle = anchor.style || "";
          const matchIdx = nodes.findIndex((n) => n.text === anchorText && n.style === anchorStyle);
          tableNodes[t]._afterIndex = matchIdx >= 0 ? nodes[matchIdx].index : -1;
        } else {
          tableNodes[t]._afterIndex = -1;
        }
      }
    }

    // Page numbers for tables (Desktop only)
    if (hasPageApi && tableNodes.length > 0 && !skipPageNumbers) {
      const BATCH_SIZE = 50;
      for (let batchStart = 0; batchStart < tableNodes.length; batchStart += BATCH_SIZE) {
        const batchEnd = Math.min(batchStart + BATCH_SIZE, tableNodes.length);
        const batchEntries = [];

        for (let b = batchStart; b < batchEnd; b++) {
          const tn = tableNodes[b];
          const table = tables.items[tn.position.tableIndex];
          const range = table.getRange();
          const pages = range.pages;
          pages.load("items");
          batchEntries.push({ node: tn, pages });
        }

        await context.sync();

        for (const entry of batchEntries) {
          for (const page of entry.pages.items) {
            page.load("index");
          }
        }

        await context.sync();

        for (const { node, pages } of batchEntries) {
          if (pages.items.length > 0) {
            const indices = pages.items.map((pg) => pg.index);
            node.position.pageRange = [Math.min(...indices), Math.max(...indices)];
          }
        }
      }
    }
  });

  // Merge tables into nodes by document position
  const allNodes = [...nodes];
  for (const tn of tableNodes) {
    const insertAfter = allNodes.findIndex((n) => n.index === tn._afterIndex);
    delete tn._afterIndex;
    if (insertAfter >= 0) {
      allNodes.splice(insertAfter + 1, 0, tn);
    } else {
      allNodes.unshift(tn);
    }
  }

  for (const n of allNodes) delete n.index;

  const styles = Object.entries(styleCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([name, count]) => ({ name, count }));

  const totalChars = allNodes.reduce((sum, n) => sum + (n.charCount || 0), 0);
  const totalWords = allNodes.reduce((sum, n) => sum + (n.wordCount || 0), 0);
  const totalEstimatedLines = allNodes.reduce((sum, n) => {
    if (n.position && n.position.lineRange) {
      return Math.max(sum, n.position.lineRange[1]);
    }
    return sum;
  }, 0);

  const totalPages = allNodes.reduce((max, n) => {
    if (n.position && n.position.pageRange) {
      return Math.max(max, n.position.pageRange[1]);
    }
    return max;
  }, 1);

  const meta = {
    totalNodes: allNodes.length,
    totalParagraphs: allNodes.filter((n) => n.type === "paragraph").length,
    totalListItems: allNodes.filter((n) => n.type === "list_item").length,
    totalTables: allNodes.filter((n) => n.type === "table").length,
    totalTocEntries: toc.length,
    hasToc: toc.length > 0,
    totalCharacters: totalChars,
    totalWords: totalWords,
    totalEstimatedLines,
    totalPages,
  };

  storeNodeReference(allNodes);

  return {
    version: 4,
    extractedAt: new Date().toISOString(),
    meta,
    styles,
    toc,
    nodes: allNodes,
  };
};
